import { EMatchingType } from '@enums/campaign-analytics.enum';
import { ICtaResultApi } from '@interfaces/campaign-analytics-api.interface';
import { maxBy } from 'lodash';

export function pickMaxScoreCtaResult(list: ICtaResultApi[]): ICtaResultApi | undefined {
  return maxBy(list, (c) => c.ctaAnalytics?.matchingScore ?? Number.NEGATIVE_INFINITY);
}

/**
 * The deliverable group a content is shown under (Contents tab badge, CTA dialog default): the best-scoring
 * matched group, else the best-scoring bonus group, else the best-scoring group.
 */
export function getDefaultSelectedCtaId(ctas: ICtaResultApi[]): string | null {
  if (!ctas.length) {
    return null;
  }

  const bonus: ICtaResultApi[] = ctas.filter((c) => c.matchingType === EMatchingType.bonus);
  const matched: ICtaResultApi[] = ctas.filter((c) => c.matchingType === EMatchingType.matched);

  let chosen: ICtaResultApi | undefined;

  if (matched.length) {
    chosen = pickMaxScoreCtaResult(matched);
  } else if (bonus.length) {
    chosen = pickMaxScoreCtaResult(bonus);
  } else {
    chosen = pickMaxScoreCtaResult(ctas);
  }

  return chosen?.ctaId ?? null;
}

/**
 * Whether a content counts for one of its groups (Contents / Overview / Goals rule): matched always; bonus (an extra)
 * only when no group matched the content, and then only under its primary group, so one content is never an extra
 * next to the group it was delivered for. Optional groups skip this: they have no execution to fill, so the backend
 * marks every fitting content bonus, and each one is a delivery of that group.
 */
export function isCtaResultCounted(ctas: ICtaResultApi[], ctaResult: ICtaResultApi, optional = false): boolean {
  if (ctaResult.matchingType !== EMatchingType.bonus || optional) {
    return true;
  }

  return (
    !ctas.some((cta) => cta.matchingType === EMatchingType.matched) && ctaResult.ctaId === getDefaultSelectedCtaId(ctas)
  );
}
